import React, {
  memo,
  HTMLProps,
  PropsWithRef,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { WeekStartDay } from './types';
import { DateTime } from 'core/components/DateTime';

export type DatePickerProps = {
  /**
   * This function is called when the selected date is changed.
   */
  onChange: (date: Date) => void;
  /**
   * The selected date.
   */
  value: Date | undefined;
  /**
   * The minimum date that can be selected (inclusive).
   * Default is 1st January 1900
   */
  minDate?: Date;
  /**
   * The maximum date that can be selected (inclusive).
   * Default is 100 years from now
   */
  maxDate?: Date;
  /**
   * Week starts from which day
   */
  weekStartsFrom?: WeekStartDay;
  /**
   * If the DatePicker is disabled
   */
  disabled?: boolean;

  // custom props

  refCalendar?: any;
  refCalendarMenu?: any;
  onClick?: any;
  hideCalendar?: boolean;
  // date: Date | undefined;
  label?: string;
  placeholder: string;
  name: string;
  onBlur?: any;
  error?: any;
  message?: any;
  activeStartDate?: Date;
  isedit?: boolean;
  datewithtime?: boolean;
  timevalue?: any;
  timeOnChange?: any;
  disabledTime?: boolean;
  timeRequired?: boolean;
  formik?: boolean;
  isW100?: boolean;
  iconTop?: boolean;
  minMaxNotRequired?: boolean;
  minTime?: any;
  isW200?: boolean;
  customHeight?: boolean;
  defaultValueNotRequired?: boolean;
  isClearable?: boolean;
  disabledDates?: Date[];
  is4vh?: boolean;
  debounceDelay?: number;

} & PropsWithRef<
  Omit<
    HTMLProps<HTMLInputElement>,
    'onChange' | 'selected' | 'options' | 'value' | 'type' | 'name' | 'disabled'
  >
>;

/**
 * DatePicker component to pick dates
 */
const DatePicker = memo((props: DatePickerProps) => {

  const { onChange, value = props?.defaultValueNotRequired ? "" : new Date(), minDate, maxDate } = props;
  const onChangeRef = useRef(onChange);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceDelay = props?.debounceDelay ?? 900;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = useCallback((date: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onChangeRef.current(date);
    }, debounceDelay);
  }, [debounceDelay]);

  return (
    <DateTime
      defaultValueNotRequired={props?.defaultValueNotRequired}
      isClearable={props?.isClearable}
      value={value}
      onChange={handleChange}
      error={props?.error}
      datewithtime={props?.datewithtime}
      message={props?.message}
      minDate={minDate}
      maxDate={maxDate}
      customHeight={props?.customHeight}
      label={props?.label}
      disabledDates={props?.disabledDates}
      disabled={props?.disabled}
      is4vh={props?.is4vh}
    />
  );
});

export default DatePicker;